import { api } from '@/lib/api'
import { defaultPaginationMeta, parseMasterCatalogResponse } from '@/lib/api/masterCatalog'

/** @typedef {import('@/types/api').Customer} Customer */

export const CUSTOMER_GENDER_OPTIONS = [
  { value: 'ladies', label: 'Ladies' },
  { value: 'gents', label: 'Gents' },
  { value: 'unknown', label: 'Unknown' },
]

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ items: Customer[], meta: import('@/types/api').PaginationMeta }>}
 */
export async function fetchCustomersPage(params = {}) {
  const { data } = await api.get('/masters/customers', { params })
  const parsed = parseMasterCatalogResponse(data, 'customers')
  return {
    items: parsed.items,
    meta: defaultPaginationMeta(parsed.meta ?? {}),
  }
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<Customer[]>}
 */
export async function fetchCustomersList(params = {}) {
  const { data } = await api.get('/masters/customers', { params })
  return data.data.customers
}

/**
 * @returns {Promise<Customer[]>}
 */
export async function fetchCustomers() {
  return fetchCustomersList()
}

/**
 * @param {number} customerId
 * @returns {Promise<Customer>}
 */
export async function fetchCustomer(customerId) {
  const { data } = await api.get(`/masters/customers/${customerId}`)
  return data.data.customer
}

/**
 * @param {Partial<Customer>} payload
 * @returns {Promise<Customer>}
 */
export async function createCustomer(payload) {
  const { data } = await api.post('/masters/customers', payload)
  return data.data.customer
}

/**
 * @param {number} customerId
 * @param {Partial<Customer>} payload
 * @returns {Promise<Customer>}
 */
export async function updateCustomer(customerId, payload) {
  const { data } = await api.put(`/masters/customers/${customerId}`, payload)
  return data.data.customer
}

/**
 * @param {number} customerId
 * @returns {Promise<void>}
 */
export async function deleteCustomer(customerId) {
  await api.delete(`/masters/customers/${customerId}`)
}

/**
 * @param {{ name: string, phone: string, gender: 'ladies' | 'gents' }} payload
 * @returns {Promise<Customer>}
 */
export async function quickRegisterCustomer(payload) {
  const { data } = await api.post('/customers/quick-register', payload)
  return data.data.customer
}

/**
 * @param {string} query
 * @param {{ limit?: number }} [options]
 * @returns {Promise<Customer[]>}
 */
export async function searchCustomers(query, options = {}) {
  const params = { q: query, ...options }
  const { data } = await api.get('/customers/search', { params })
  return data.data.customers
}
